import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const contactInfo = [
        {
            icon: 'fa-phone-volume',
            title: 'Call Us',
            details: ['+971 4 326 2259', '+971 56 858 3383'],
            action: 'tel:+97143262259',
            actionText: 'Call Now',
            color: 'primary-red',
            gradient: 'linear-gradient(135deg, #FF6B6B, #FF4757)'
        },
        {
            icon: 'fa-envelope',
            title: 'Email Us',
            details: ['sales@lamsatalbedaya.com', 'info@lamsat.com'],
            action: 'mailto:sales@lamsatalbedaya.com',
            actionText: 'Send Email',
            color: 'primary-blue',
            gradient: 'linear-gradient(135deg, #4A6FFF, #3B5BDB)'
        },
        {
            icon: 'fa-building',
            title: 'Visit Office',
            details: ['Office No.33, Second Floor', 'Building Smark 2', 'Ras Al Khor, Dubai, UAE'],
            action: 'https://goo.gl/maps/example',
            actionText: 'Get Directions',
            color: 'primary-blue',
            gradient: 'linear-gradient(135deg, #4A6FFF, #3B5BDB)'
        },
        {
            icon: 'fa-clock',
            title: 'Working Hours',
            details: ['Sunday - Thursday: 8:00 AM - 6:00 PM', 'Friday: 9:00 AM - 1:00 PM', 'Saturday: Closed'],
            color: 'primary-red',
            gradient: 'linear-gradient(135deg, #FF6B6B, #FF4757)'
        }
    ];

    const services = [
        'HVAC Systems',
        'Electrical Works',
        'Plumbing Services',
        'Waterproofing Solutions',
        'Civil Construction',
        'Painting Services',
        'Metal & Aluminum Work',
        'Carpentry & Woodwork',
        'Other Services'
    ];

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        if (!formData.service) newErrors.service = 'Please select a service';

        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitSuccess(true);
            setFormData({ name: '', email: '', phone: '', service: '', message: '' });

            setTimeout(() => setSubmitSuccess(false), 5000);
        }, 1500);
    };

    return (
        <>
            <Helmet>
                <title>Contact Us | LAMSAT Engineering Services Dubai</title>
                <meta
                    name="description"
                    content="Contact LAMSAT for engineering services in Dubai. Get in touch for HVAC, electrical, plumbing, construction, and waterproofing solutions."
                />
            </Helmet>

            {/* Hero Section */}
            <section className="py-5 bg-primary-blue" style={{ marginTop: '80px' }}>
                <div className="container">
                    <div className="text-center text-white">
                        <motion.h1
                            className="display-5 fw-bold mb-3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            Get In <span style={{ color: 'var(--primary-red)' }}>Touch</span>
                        </motion.h1>
                        <motion.p
                            className="lead text-off-white mb-0"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                        >
                            Connect with our engineering experts for your project needs
                        </motion.p>
                    </div>
                </div>
            </section>

            {/* Main Contact Section */}
            <section className="py-5 bg-off-white">
                <div className="container">
                    <div className="row g-4">
                        {/* Left Column - Contact Information */}
                        <motion.div
                            className="col-lg-4"
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="mb-5">
                                <h2 className="section-title mb-3">Contact Information</h2>
                                <p className="text-cool-grey">
                                    Multiple ways to reach our engineering team
                                </p>
                            </div>

                            {/* Emergency Card - Improved Layout */}
                            <motion.div
                                className="card border-0 shadow-lg mb-4 overflow-hidden"
                                whileHover={{ y: -5 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <div className="position-relative">
                                    {/* Gradient Background */}
                                    <div className="position-absolute top-0 start-0 w-100 h-100"
                                         style={{
                                             background: 'linear-gradient(135deg, #FF6B6B, #FF4757, #FF3838)',
                                             opacity: '0.08',
                                             zIndex: '0'
                                         }}>
                                    </div>
                                    
                                    <div className="card-body p-4 position-relative" style={{ zIndex: '1' }}>
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="flex-shrink-0">
                                                <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        background: 'linear-gradient(135deg, #FF6B6B, #FF4757)'
                                                    }}>
                                                    <i className="fas fa-exclamation-triangle text-white fs-5"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <h5 className="text-dark-charcoal fw-bold mb-0">24/7 Emergency Services</h5>
                                                    <span className="badge px-3 py-1 small ms-2"
                                                          style={{
                                                              background: 'rgba(255, 71, 87, 0.15)',
                                                              color: '#FF4757',
                                                              border: '1px solid rgba(255, 71, 87, 0.3)',
                                                              whiteSpace: 'nowrap',
                                                              fontWeight: '600'
                                                          }}>
                                                        24/7
                                                    </span>
                                                </div>
                                                <p className="text-cool-grey mb-3 small">
                                                    For urgent HVAC, electrical, or plumbing emergencies
                                                </p>
                                                <motion.a
                                                    href="tel:+971568583383"
                                                    className="btn d-flex align-items-center justify-content-center gap-2 w-100"
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #FF6B6B, #FF4757)',
                                                        color: 'white',
                                                        border: 'none',
                                                        fontWeight: '600',
                                                        padding: '10px 20px'
                                                    }}
                                                >
                                                    <i className="fas fa-phone-alt"></i>
                                                    <span>+971 56 858 3383</span>
                                                </motion.a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* WhatsApp Card */}
                            <motion.div
                                className="card border-0 shadow-lg mb-5 overflow-hidden"
                                whileHover={{ y: -5 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <div className="position-relative">
                                    {/* Gradient Background */}
                                    <div className="position-absolute top-0 start-0 w-100 h-100"
                                         style={{
                                             background: 'linear-gradient(135deg, #25D366, #128C7E, #075E54)',
                                             opacity: '0.08',
                                             zIndex: '0'
                                         }}>
                                    </div>
                                    
                                    <div className="card-body p-4 position-relative" style={{ zIndex: '1' }}>
                                        <div className="d-flex align-items-start gap-3">
                                            <div className="flex-shrink-0">
                                                <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        width: '50px', 
                                                        height: '50px',
                                                        background: 'linear-gradient(135deg, #25D366, #128C7E)'
                                                    }}>
                                                    <i className="fab fa-whatsapp text-white fs-5"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1">
                                                <h5 className="text-dark-charcoal fw-bold mb-2">Quick WhatsApp Chat</h5>
                                                <p className="text-cool-grey mb-3 small">
                                                    Get instant responses during business hours
                                                </p>
                                                <motion.a
                                                    href="https://wa.me/971563670933"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    style={{ 
                                                        background: 'linear-gradient(135deg, #25D366, #128C7E)',
                                                        color: 'white',
                                                        border: 'none',
                                                        fontWeight: '600',
                                                        padding: '10px 20px'
                                                    }}
                                                >
                                                    <i className="fab fa-whatsapp"></i>
                                                    <span>Start WhatsApp Chat</span>
                                                </motion.a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Contact Information Cards */}
                            <div className="row g-3">
                                {contactInfo.map((info, index) => (
                                    <motion.div
                                        key={index}
                                        className="col-12 col-sm-6 col-lg-12"
                                        whileHover={{ y: -3 }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{
                                            duration: 0.4,
                                            delay: 0.4 + (index * 0.1)
                                        }}
                                    >
                                        <div className="card border-0 shadow-sm h-100 overflow-hidden">
                                            <div className="position-relative">
                                                {/* Gradient Background */}
                                                <div className="position-absolute top-0 start-0 w-100 h-100"
                                                     style={{
                                                         background: info.gradient,
                                                         opacity: '0.08',
                                                         zIndex: '0'
                                                     }}>
                                                </div>
                                                
                                                <div className="card-body p-3 p-sm-4 position-relative" style={{ zIndex: '1' }}>
                                                    <div className="d-flex align-items-start gap-3">
                                                        <div className="flex-shrink-0">
                                                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    background: info.gradient
                                                                }}>
                                                                <i className={`fas ${info.icon} text-white`}></i>
                                                            </div>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h6 className="fw-bold mb-2"
                                                                style={{
                                                                    color: info.color === 'primary-red' ? '#FF4757' : '#3B5BDB'
                                                                }}>
                                                                {info.title}
                                                            </h6>
                                                            <div className="mb-2">
                                                                {info.details.map((detail, i) => (
                                                                    <p key={i} className="text-cool-grey small mb-1">
                                                                        {detail}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                            {info.action && (
                                                                <a
                                                                    href={info.action}
                                                                    target={info.action.includes('http') ? '_blank' : '_self'}
                                                                    rel={info.action.includes('http') ? 'noopener noreferrer' : ''}
                                                                    className="btn btn-outline-custom btn-sm d-inline-flex align-items-center gap-1"
                                                                    style={{ 
                                                                        padding: '6px 14px',
                                                                        fontSize: '0.8rem',
                                                                        borderColor: info.color === 'primary-red' ? '#FF4757' : '#3B5BDB',
                                                                        color: info.color === 'primary-red' ? '#FF4757' : '#3B5BDB',
                                                                        fontWeight: '500'
                                                                    }}
                                                                >
                                                                    <span>{info.actionText}</span>
                                                                    <i className="fas fa-arrow-right fa-xs ms-1"></i>
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right Column - Contact Form & Map */}
                        <motion.div
                            className="col-lg-8"
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="row g-4">
                                {/* Contact Form */}
                                <div className="col-12">
                                    <div className="card border-0 shadow-lg">
                                        <div className="card-body p-4 p-md-5">
                                            <h3 className="section-title mb-3">Send Us a Message</h3>
                                            <p className="text-cool-grey mb-4">
                                                Fill out the form and our team will contact you within 24 hours
                                            </p>

                                            {submitSuccess && (
                                                <motion.div
                                                    className="alert alert-success d-flex align-items-center gap-3"
                                                    initial={{ opacity: 0, scale: 0.9 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                >
                                                    <i className="fas fa-check-circle text-success fa-2x"></i>
                                                    <div>
                                                        <h6 className="mb-1 text-success">Message Sent Successfully!</h6>
                                                        <p className="mb-0 text-success small">We'll contact you within 24 hours.</p>
                                                    </div>
                                                </motion.div>
                                            )}

                                            <form onSubmit={handleSubmit} className="contact-form mt-4">
                                                <div className="row mb-3">
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="name" className="form-label fw-bold text-primary-blue">
                                                            Full Name *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="name"
                                                            name="name"
                                                            value={formData.name}
                                                            onChange={handleChange}
                                                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                                            placeholder="Enter your name"
                                                        />
                                                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="email" className="form-label fw-bold text-primary-blue">
                                                            Email Address *
                                                        </label>
                                                        <input
                                                            type="email"
                                                            id="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                                            placeholder="Enter your email"
                                                        />
                                                        {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                                                    </div>
                                                </div>

                                                <div className="row mb-3">
                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="phone" className="form-label fw-bold text-primary-blue">
                                                            Phone Number *
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            id="phone"
                                                            name="phone"
                                                            value={formData.phone}
                                                            onChange={handleChange}
                                                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                                                            placeholder="+971 XX XXX XXXX"
                                                        />
                                                        {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
                                                    </div>

                                                    <div className="col-md-6 mb-3">
                                                        <label htmlFor="service" className="form-label fw-bold text-primary-blue">
                                                            Service Interested In *
                                                        </label>
                                                        <select
                                                            id="service"
                                                            name="service"
                                                            value={formData.service}
                                                            onChange={handleChange}
                                                            className={`form-control ${errors.service ? 'is-invalid' : ''}`}
                                                        >
                                                            <option value="">Select a service</option>
                                                            {services.map((service, index) => (
                                                                <option key={index} value={service}>{service}</option>
                                                            ))}
                                                        </select>
                                                        {errors.service && <div className="invalid-feedback d-block">{errors.service}</div>}
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <label htmlFor="message" className="form-label fw-bold text-primary-blue">
                                                        Project Details / Message
                                                    </label>
                                                    <textarea
                                                        id="message"
                                                        name="message"
                                                        rows="4"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className="form-control"
                                                        placeholder="Tell us about your project requirements, timeline, and any specific needs..."
                                                    />
                                                </div>

                                                <motion.button
                                                    type="submit"
                                                    className="btn btn-primary-custom w-100 d-flex align-items-center justify-content-center gap-2"
                                                    disabled={isSubmitting}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <i className="fas fa-spinner fa-spin"></i>
                                                            <span>Sending...</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-paper-plane"></i>
                                                            <span>Send Message</span>
                                                        </>
                                                    )}
                                                </motion.button>
                                            </form>
                                        </div>
                                    </div>
                                </div>

                                {/* Map Section */}
                                <div className="col-12">
                                    <div className="card border-0 shadow-lg">
                                        <div className="card-body p-4 p-md-5">
                                            <h3 className="section-title mb-3">Our Location</h3>
                                            <div className="map-container mb-4" style={{ height: '250px', borderRadius: '8px', overflow: 'hidden' }}>
                                                <iframe
                                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3608.5795113708264!2d55.37722331548441!3d25.2479179838665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d2b8c5c5c5d%3A0x3e5f5d2b8c5c5c5d!2sDubai%2C%20United%20Arab%20Emirates!5e0!3m2!1sen!2s!4v1647851234567!5m2!1sen!2s"
                                                    allowFullScreen=""
                                                    loading="lazy"
                                                    title="LAMSAT Office Location"
                                                    className="w-100 h-100"
                                                    style={{ border: 'none' }}
                                                ></iframe>
                                            </div>
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                                                <div className="d-flex align-items-start gap-2">
                                                    <i className="fas fa-map-marker-alt text-primary-red mt-1"></i>
                                                    <p className="text-cool-grey mb-0 small">
                                                        Office No.33, Second Floor, Building Smark 2, Ras Al Khor, Dubai, UAE
                                                    </p>
                                                </div>
                                                <a
                                                    href="https://goo.gl/maps/example"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-custom btn-sm d-flex align-items-center gap-2"
                                                >
                                                    <i className="fas fa-directions"></i>
                                                    Get Directions
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-5 bg-primary-blue">
                <div className="container">
                    <motion.div
                        className="text-center text-white"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="mb-3">Ready to Start Your Project?</h2>
                        <p className="mb-4 text-off-white">Contact us today for a free consultation and quote</p>
                        <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
                            <motion.a
                                href="tel:+97143262259"
                                className="btn"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    background: 'linear-gradient(135deg, #FF6B6B, #FF4757)',
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: '600',
                                    padding: '12px 24px'
                                }}
                            >
                                <i className="fas fa-phone-alt me-2"></i>
                                Call Now
                            </motion.a>
                            <motion.a
                                href="mailto:sales@lamsatalbedaya.com"
                                className="btn btn-outline-custom"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    borderColor: 'white',
                                    color: 'white',
                                    border: '2px solid white',
                                    padding: '12px 24px',
                                    fontWeight: '500'
                                }}
                            >
                                <i className="fas fa-envelope me-2"></i>
                                Email Us
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </>
    );
};

export default Contact;